import { useSnackbar } from "notistack";
import { Close as CloseIcon } from "@mui/icons-material";
import React, { FC, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { useCreateSurvey } from "@api/hooks/doubleMateriality";
import { useUserStore } from "@store/user";

interface CreateSurveyModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (year: number) => void;
  existingYears?: number[];
}

export const CreateSurveyModal: FC<CreateSurveyModalProps> = ({
  open,
  onClose,
  onSuccess,
  existingYears = [],
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const { createSurvey, isPendingCreateSurvey } = useCreateSurvey(user);

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<string>(String(currentYear));
  const [error, setError] = useState<string>("");

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;

    setYear(value);
    setError("");
  };

  const validateYear = (): boolean => {
    const yearNum = parseInt(year, 10);

    if (!year || isNaN(yearNum)) {
      setError(t("doubleMateriality.yearRequired", "Please enter a valid year"));

      return false;
    }

    if (yearNum < 2000 || yearNum > currentYear + 10) {
      setError(
        t(
          "doubleMateriality.yearOutOfRange",
          "Year must be between 2000 and {{maxYear}}",
          { maxYear: currentYear + 10 },
        ),
      );

      return false;
    }

    if (existingYears.includes(yearNum)) {
      setError(
        t(
          "doubleMateriality.yearAlreadyExists",
          "A survey for year {{year}} already exists",
          { year: yearNum },
        ),
      );

      return false;
    }

    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateYear()) {
      return;
    }

    try {
      const yearNum = parseInt(year, 10);

      await createSurvey({ year: yearNum });

      enqueueSnackbar(
        t(
          "doubleMateriality.surveyCreated",
          "Survey for {{year}} created successfully",
          { year: yearNum },
        ),
        { variant: "success" },
      );

      onSuccess(yearNum);
      handleClose();
    } catch (error: unknown) {
      enqueueSnackbar(
        t("doubleMateriality.surveyCreateError", "Failed to create survey"),
        { variant: "error" },
      );
      console.error("Survey creation error:", error);
    }
  };

  const handleClose = (): void => {
    setYear(String(currentYear));
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t("doubleMateriality.defineYear", "Define Year")}
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={isPendingCreateSurvey}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t(
              "doubleMateriality.defineYearDescription",
              "To get started with double materiality, please define the reporting year first.",
            )}
          </Typography>

          <TextField
            label={t("doubleMateriality.reportingYear", "Reporting Year")}
            type="number"
            value={year}
            onChange={handleYearChange}
            error={!!error}
            helperText={error}
            disabled={isPendingCreateSurvey}
            fullWidth
            autoFocus
            InputProps={{
              inputProps: {
                min: 2000,
                max: currentYear + 10,
              },
            }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isPendingCreateSurvey}
          variant="outlined"
        >
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isPendingCreateSurvey || !year}
          variant="contained"
          color="primary"
        >
          {t("common.start", "Start")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
